import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Spacer,
  Button,
  InputGroup,
  InputLeftElement,
  Input,
  HStack,
  Badge,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  List,
  ListItem,
  ListIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { FiSearch, FiFilter, FiPlus, FiEdit2 } from 'react-icons/fi';
import { Users, Shield, Settings, UserCheck, CheckCircle } from 'lucide-react';
import PageLayout from '../components/PageLayout';

const dummyRoleStats = {
  totalUsers: 3420,
  admins: 24,
  clubs: 3396,
};

const dummyRoleDefinitions = [
  {
    role: 'Admin',
    level: 'Medium',
    icon: Settings,
    color: 'purple.500',
    responsibilities: [
      'Manage user settings',
      'Configure chatbots',
      'View analytics',
      'Manage user members',
      'Access knowledge base',
    ],
  },
  {
    role: 'user Account',
    level: 'Basic',
    icon: Users,
    color: 'green.500',
    responsibilities: [
      'Use chatbots',
      'Voice agent access',
      'View user content',
      'Participate in events',
      'Basic profile settings',
    ],
  },
];

const dummyClubAccounts = [
  { id: 1, name: 'John Smith', email: 'john.smith@email.com', role: 'Super Admin', clubName: 'System', status: 'active', lastActive: '2 minutes ago' },
  { id: 2, name: 'Sarah Johnson', email: 'sarah.j@techclub.com', role: 'Admin', clubName: 'Tech Club Alpha', status: 'active', lastActive: '1 hour ago' },
  { id: 3, name: 'Mike Wilson', email: 'mike.w@sportsclub.com', role: 'Admin', clubName: 'Sports Club Beta', status: 'active', lastActive: '3 hours ago' },
  { id: 4, name: 'Emily Davis', email: 'emily.d@artclub.com', role: 'user Account', clubName: 'Art Club Gamma', status: 'inactive', lastActive: '2 days ago' },
  { id: 5, name: 'David Chen', email: 'david.c@musicclub.com', role: 'user Account', clubName: 'Music Club Delta', status: 'active', lastActive: '30 minutes ago' },
];

const roleColorScheme = {
  'Super Admin': 'red',
  'Admin': 'purple',
  'user Account': 'green',
};

const ClubAccountManagement = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [clubAccounts, setClubAccounts] = useState(dummyClubAccounts);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [clubNameFilter, setClubNameFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClubAccount, setEditingClubAccount] = useState(null);
  const [newClubAccount, setNewClubAccount] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    clubName: '',
    customId: '',
    password: '',
    confirmPassword: '',
    features: {
      voiceChat: false,
      chatbot: false,
    },
    totalTokens: 1000,
    role: 'Club Account',
    retellApiKey: '',
    projectApiKey: '',
    voiceflowApiKey: '',
    retellAgentId: '',
    voiceflowAgentId: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClubAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!newClubAccount.name || !newClubAccount.email || !newClubAccount.clubName) {
        toast({
            title: 'Error',
            description: 'Please fill in required fields',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (newClubAccount.password && newClubAccount.password !== newClubAccount.confirmPassword) {
        toast({
            title: 'Error',
            description: 'Passwords do not match',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (clubAccounts.some(account => account.email === newClubAccount.email || account.clubName === newClubAccount.clubName)) {
         toast({
            title: 'Error',
            description: 'user Account with this email or user name already exists',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    const clubAccountToAdd = {
      id: clubAccounts.length + 1,
      ...newClubAccount,
      lastActive: 'Just now',
    };

    setClubAccounts([...clubAccounts, clubAccountToAdd]);
    toast({
      title: 'user Account Created',
      description: 'New user account has been created successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    setNewClubAccount({
      name: '',
      email: '',
      mobileNumber: '',
      clubName: '',
      customId: '',
      password: '',
      confirmPassword: '',
      features: {
        voiceChat: false,
        chatbot: false,
      },
      totalTokens: 1000,
      role: 'user Account',
      retellApiKey: '',
      projectApiKey: '',
      voiceflowApiKey: '',
      retellAgentId: '',
      voiceflowAgentId: '',
    });
    onClose();
  };

  const handleStatusChange = (clubAccountId, newStatus) => {
    setClubAccounts(clubAccounts.map(account =>
      account.id === clubAccountId
        ? { ...account, status: newStatus }
        : account
    ));
    toast({
      title: 'Status Updated',
      description: `user Account status changed to ${newStatus}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleEditClubAccount = (account) => {
    setEditingClubAccount({
      ...account,
      mobileNumber: account.mobileNumber || '',
      customId: account.customId || '',
      features: {
        voiceChat: account.features?.voiceChat || false,
        chatbot: account.features?.chatbot || false,
      },
      totalTokens: account.totalTokens || 1000,
      retellApiKey: account.retellApiKey || '',
      projectApiKey: account.projectApiKey || '',
      voiceflowApiKey: account.voiceflowApiKey || '',
      retellAgentId: account.retellAgentId || '',
      voiceflowAgentId: account.voiceflowAgentId || '',
      password: '',
      confirmPassword: '',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateClubAccount = (e) => {
    e.preventDefault();

    if (!editingClubAccount.features.voiceChat && !editingClubAccount.features.chatbot) {
      toast({
        title: 'Validation Error',
        description: 'At least one feature (Voice Chat or Chatbot) must be enabled.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

     // Optional API key validation based on enabled features
     if (editingClubAccount.features.voiceChat && !editingClubAccount.retellApiKey) {
         toast({
            title: 'Validation Error',
            description: 'Retell API Key is required for Voice Chat feature',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
        return;
     }

     if (editingClubAccount.features.chatbot && !editingClubAccount.voiceflowApiKey) {
         toast({
            title: 'Validation Error',
            description: 'Voiceflow API Key is required for Chatbot feature',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
        return;
     }

    // In a real app, you'd send the updated data to your backend
    console.log('Updating user account:', editingClubAccount);

    setClubAccounts(clubAccounts.map(account =>
      account.id === editingClubAccount.id
        ? editingClubAccount
        : account
    ));

    toast({
      title: 'Club Account Updated',
      description: 'Club account has been updated successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });

    setIsEditModalOpen(false);
    setEditingClubAccount(null);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingClubAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditFeatureChange = (feature) => {
    setEditingClubAccount(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const handleDeleteClubAccount = (clubAccountId) => {
    if (window.confirm('Are you sure you want to delete this user account?')) {
      // In a real app, you'd send the delete request to your backend
      console.log('Deleting user account with id:', clubAccountId);
      setClubAccounts(clubAccounts.filter(account => account.id !== clubAccountId));
      toast({
        title: 'user Account Deleted',
        description: 'user account has been deleted successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const handleClubNameFilterChange = (e) => {
    setClubNameFilter(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const filteredClubAccounts = clubAccounts.filter(account => {
    const searchMatch = account.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        account.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        account.clubName.toLowerCase().includes(searchQuery.toLowerCase());

    const roleMatch = roleFilter === 'all' || account.role === roleFilter;
    const clubNameMatch = clubNameFilter === 'all' || account.clubName === clubNameFilter;
    const statusMatch = statusFilter === 'all' || account.status === statusFilter;

    return searchMatch && roleMatch && clubNameMatch && statusMatch;
  });

  const cardBg = useColorModeValue('white', 'gray.700');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const statCardBg = useColorModeValue('gray.100', 'gray.800');

  const roleBadgeColor = (role) => {
    switch (role) {
      case 'Super Admin':
        return 'red';
      case 'Admin':
        return 'purple';
      case 'user Account':
        return 'green';
      default:
        return 'gray';
    }
  };

  const uniqueClubNames = ['all', ...new Set(dummyClubAccounts.map(account => account.clubName))];

  return (
    <PageLayout title="user Account Management">
      <VStack spacing={6} align="stretch">
        <Flex alignItems="center">
          <Box>
            <Text color={textColor} mt={1}>Manage user accounts and permissions</Text>
          </Box>
          <Spacer />
          {/* <Button leftIcon={<FiPlus />} colorScheme="blue" size="md" onClick={onOpen}>
            Add Club Account
          </Button> */}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mt={4}>
            <Card bg={statCardBg} boxShadow="md">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>Total user Accounts</StatLabel>
                  <StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>{dummyRoleStats.totalUsers}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={statCardBg} boxShadow="md">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>Admin Accounts</StatLabel>
                   <HStack spacing={2}>
                      <StatNumber fontSize="2xl" fontWeight="bold" color="purple.500">{dummyRoleStats.admins}</StatNumber>
                       <Icon as={Settings} boxSize={6} color="purple.500" />
                   </HStack>
                </Stat>
              </CardBody>
            </Card>
             <Card bg={statCardBg} boxShadow="md">
                <CardBody>
                    <Stat>
                        <StatLabel color={textColor}>user Accounts (Basic)</StatLabel>
                         <HStack spacing={2}>
                            <StatNumber fontSize="2xl" fontWeight="bold" color="green.500">{dummyRoleStats.clubs.toLocaleString()}</StatNumber>
                             <Icon as={UserCheck} boxSize={6} color="green.500" />
                         </HStack>
                    </Stat>
                </CardBody>
            </Card>
        </SimpleGrid>

        <Card bg={cardBg} boxShadow="lg">
          <CardHeader>
            <Flex alignItems="center">
              <Heading size="md" color={headingColor}>user Account List</Heading>
              <Spacer />
              <InputGroup flex="1" maxW="xs" mr={4}>
                <InputLeftElement
                  pointerEvents="none"
                  children={<FiSearch color="gray.300" />}
                />
                <Input
                  type="text"
                  placeholder="Search user accounts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  bg={useColorModeValue('gray.100', 'gray.700')}
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                  _placeholder={{ color: 'gray.400' }}
                />
              </InputGroup>
              <Select
                value={roleFilter}
                onChange={handleRoleFilterChange}
                maxW="150px"
                mr={2}
              >
                <option value="all">All Roles</option>
                <option value="Admin">Admin</option>
                <option value="user Account">user Account</option>
              </Select>
              <Select
                value={clubNameFilter}
                onChange={handleClubNameFilterChange}
                maxW="150px"
                mr={2}
              >
                <option value="all">All users</option>
                {uniqueClubNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Select>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                maxW="150px"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </Flex>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th color={textColor}>user Account Name</Th>
                    <Th color={textColor}>Role</Th>
                    <Th color={textColor}>user</Th>
                    <Th color={textColor}>Status</Th>
                    <Th color={textColor}>Last Active</Th>
                    <Th color={textColor}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredClubAccounts.map(account => (
                    <Tr key={account.id} _hover={{ bg: useColorModeValue('gray.50', 'gray.700/50') }}>
                      <Td py={4} px={4}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color={headingColor}>{account.name}</Text>
                          <Text fontSize="sm" color={textColor}>{account.email}</Text>
                        </VStack>
                      </Td>
                      <Td py={4} px={4}>
                        <Badge colorScheme={roleBadgeColor(account.role)} fontSize="xs">{account.role}</Badge>
                      </Td>
                      <Td py={4} px={4} color={textColor}>{account.clubName}</Td>
                      <Td py={4} px={4}>
                        <Button
                          size="sm"
                          colorScheme={account.status === 'active' ? 'green' : 'red'}
                          variant="outline"
                          onClick={() => handleStatusChange(account.id, account.status === 'active' ? 'inactive' : 'active')}
                        >
                          {account.status}
                        </Button>
                      </Td>
                      <Td py={4} px={4} color={textColor}>{account.lastActive}</Td>
                      <Td py={4} px={4}>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            leftIcon={<FiEdit2 />}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEditClubAccount(account)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() => handleDeleteClubAccount(account.id)}
                          >
                            Delete
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>

        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create New user Account</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Full Name (for account)</FormLabel>
                    <Input
                      name="name"
                      value={newClubAccount.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Email (for account)</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={newClubAccount.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Mobile Number</FormLabel>
                    <Input
                      name="mobileNumber"
                      value={newClubAccount.mobileNumber}
                      onChange={handleInputChange}
                      placeholder="Enter mobile number"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>user Name</FormLabel>
                    <Input
                      name="userName"
                      value={newClubAccount.clubName}
                      onChange={handleInputChange}
                      placeholder="Enter user name"
                    />
                  </FormControl>

                   <FormControl>
                    <FormLabel>Custom ID (Optional)</FormLabel>
                    <Input
                      name="customId"
                      value={newClubAccount.customId}
                      onChange={handleInputChange}
                      placeholder="Enter custom ID"
                    />
                  </FormControl>

                   <FormControl isRequired>
                    <FormLabel>Password</FormLabel>
                    <Input
                      name="password"
                      type="password"
                      value={newClubAccount.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                    />
                  </FormControl>

                   <FormControl isRequired>
                    <FormLabel>Confirm Password</FormLabel>
                    <Input
                      name="confirmPassword"
                      type="password"
                      value={newClubAccount.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Features</FormLabel>
                    <VStack align="start" spacing={2}>
                      <Checkbox
                        isChecked={newClubAccount.features.voiceChat}
                        onChange={() => setNewClubAccount(prev => ({ ...prev, features: { ...prev.features, voiceChat: !prev.features.voiceChat } }))}
                      >
                        Voice Chat
                      </Checkbox>
                      <Checkbox
                        isChecked={newClubAccount.features.chatbot}
                        onChange={() => setNewClubAccount(prev => ({ ...prev, features: { ...prev.features, chatbot: !prev.features.chatbot } }))}
                      >
                        Chatbot
                      </Checkbox>
                    </VStack>
                  </FormControl>

                   {newClubAccount.features.voiceChat && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Retell API Key</FormLabel>
                    <Input
                      value={newClubAccount.retellApiKey}
                      onChange={(e) => setNewClubAccount(prev => ({ ...prev, retellApiKey: e.target.value }))}
                      placeholder="Enter Retell API Key"
                    />
                  </FormControl>

                   <FormControl>
                    <FormLabel>Retell Agent ID</FormLabel>
                    <Input
                      value={newClubAccount.retellAgentId}
                      onChange={(e) => setNewClubAccount(prev => ({ ...prev, retellAgentId: e.target.value }))}
                      placeholder="Enter Retell Agent ID (Optional)"
                    />
                  </FormControl>
                </>
              )}

              {newClubAccount.features.chatbot && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Voiceflow API Key</FormLabel>
                    <Input
                      value={newClubAccount.voiceflowApiKey}
                      onChange={(e) => setNewClubAccount(prev => ({ ...prev, voiceflowApiKey: e.target.value }))}
                      placeholder="Enter Voiceflow API Key"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Voiceflow Agent ID</FormLabel>
                    <Input
                      value={newClubAccount.voiceflowAgentId}
                      onChange={(e) => setNewClubAccount(prev => ({ ...prev, voiceflowAgentId: e.target.value }))}
                      placeholder="Enter Voiceflow Agent ID (Optional)"
                    />
                  </FormControl>
                </>
              )}

              <FormControl isRequired>
                <FormLabel>Total Tokens</FormLabel>
                <NumberInput
                  min={0}
                  value={newClubAccount.totalTokens}
                  onChange={(value) => setNewClubAccount(prev => ({ ...prev, totalTokens: parseInt(value) }))}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Role</FormLabel>
                <Select
                  name="role"
                  value={newClubAccount.role}
                  onChange={handleInputChange}
                >
                  <option value="user Account">user Account</option>
                  <option value="Admin">Admin</option>
                </Select>
              </FormControl>

              <Button type="submit" colorScheme="blue" width="full">
                Create user Account
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>

        <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit user Account</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {editingClubAccount && (
                <form onSubmit={handleUpdateClubAccount}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Full Name (for account)</FormLabel>
                      <Input
                        value={editingClubAccount.name}
                        onChange={(e) => setEditingClubAccount({ ...editingClubAccount, name: e.target.value })}
                        placeholder="Enter full name"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Email (for account)</FormLabel>
                      <Input
                        type="email"
                        value={editingClubAccount.email}
                        onChange={(e) => setEditingClubAccount({ ...editingClubAccount, email: e.target.value })}
                        placeholder="Enter email address"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Mobile Number</FormLabel>
                      <Input
                        name="mobileNumber"
                        value={editingClubAccount.mobileNumber}
                        onChange={handleEditInputChange}
                        placeholder="Enter mobile number"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>user Name</FormLabel>
                      <Input
                        name="userName"
                        value={editingClubAccount.clubName}
                        onChange={handleEditInputChange}
                        placeholder="Enter user name"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Custom ID (Optional)</FormLabel>
                      <Input
                        name="customId"
                        value={editingClubAccount.customId}
                        onChange={handleEditInputChange}
                        placeholder="Enter custom ID"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Features</FormLabel>
                      <VStack align="start" spacing={2}>
                        <Checkbox
                          isChecked={editingClubAccount.features.voiceChat}
                          onChange={() => handleEditFeatureChange('voiceChat')}
                        >
                          Voice Chat
                        </Checkbox>
                        <Checkbox
                          isChecked={editingClubAccount.features.chatbot}
                          onChange={() => handleEditFeatureChange('chatbot')}
                        >
                          Chatbot
                        </Checkbox>
                      </VStack>
                    </FormControl>

                     {editingClubAccount.features.voiceChat && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Retell API Key</FormLabel>
                    <Input
                      value={editingClubAccount.retellApiKey}
                      onChange={(e) => setEditingClubAccount(prev => ({ ...prev, retellApiKey: e.target.value }))}
                      placeholder="Enter Retell API Key"
                    />
                  </FormControl>

                   <FormControl>
                    <FormLabel>Retell Agent ID</FormLabel>
                    <Input
                      value={editingClubAccount.retellAgentId}
                      onChange={(e) => setEditingClubAccount(prev => ({ ...prev, retellAgentId: e.target.value }))}
                      placeholder="Enter Retell Agent ID (Optional)"
                    />
                  </FormControl>
                </>
              )}

              {editingClubAccount.features.chatbot && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Voiceflow API Key</FormLabel>
                    <Input
                      value={editingClubAccount.voiceflowApiKey}
                      onChange={(e) => setEditingClubAccount(prev => ({ ...prev, voiceflowApiKey: e.target.value }))}
                      placeholder="Enter Voiceflow API Key"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Voiceflow Agent ID</FormLabel>
                    <Input
                      value={editingClubAccount.voiceflowAgentId}
                      onChange={(e) => setEditingClubAccount(prev => ({ ...prev, voiceflowAgentId: e.target.value }))}
                      placeholder="Enter Voiceflow Agent ID (Optional)"
                    />
                  </FormControl>
                </>
              )}

                    <FormControl isRequired>
                      <FormLabel>Total Tokens</FormLabel>
                      <NumberInput
                        min={0}
                        value={editingClubAccount.totalTokens}
                        onChange={(value) => setEditingClubAccount(prev => ({ ...prev, totalTokens: parseInt(value) }))}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Role</FormLabel>
                      <Select
                        name="role"
                        value={editingClubAccount.role}
                        onChange={handleEditInputChange}
                      >
                        <option value="user Account">user Account</option>
                        <option value="Admin">Admin</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Password (Leave blank to keep current)</FormLabel>
                      <Input
                        name="password"
                        type="password"
                        value={editingClubAccount.password || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter new password"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Confirm Password</FormLabel>
                      <Input
                        name="confirmPassword"
                        type="password"
                        value={editingClubAccount.confirmPassword || ''}
                        onChange={handleEditInputChange}
                        placeholder="Confirm new password"
                      />
                    </FormControl>

                    <Button type="submit" colorScheme="blue" width="full">
                      Update user Account
                    </Button>
                  </VStack>
                </form>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </PageLayout>
  );
};

export default ClubAccountManagement;
